import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.ad{align-items:stretch;}.ssr-plugin'
import '.dh{box-shadow:0px 0px 15px 0px rgba(0, 0, 0, 0.15);}.ssr-plugin'
import '.at{border-radius:1.25rem;}.ssr-plugin'
import '.t{background-color:var(--palette-white);}.ssr-plugin'
import '.al{overflow:hidden;}.ssr-plugin'
import '.dz{text-overflow:ellipsis;}.ssr-plugin'
import '.e0{width:33rem;}.ssr-plugin'
import '.e1{border-bottom:1px solid var(--gray-100);}.ssr-plugin'
import '.e2{padding-block:0.5rem;}.ssr-plugin'
import { Button } from '@app/components/core/button/Button'
import { CloseButton } from '@app/components/core/button/CloseButton'
import { BorderlessTextInput } from '@app/components/core/input/text/BorderlessTextInput'
import { MultiLineTextInput } from '@app/components/core/input/text/MultiLineTextInput'
import { Title } from '@app/components/core/typography/Title'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { styled } from 'inlines'
import { useCallback, useMemo, useRef, useState } from 'react'
import { RecipientTag } from './RecipientTag'
import { FileDisplay } from '@app/components/core/fileDisplay/FileDisplay'
import { useClient } from '@based/sdk'
import { useToast } from '@app/util/hooks/useToast'
import { Recipient } from './RecipientModel'
import { RecipientSelectionModal } from './RecipientSelectionModal'
import { FileSelector } from '@app/components/core/input/text/FileSelector'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'

export const MessageBox = ({
  recipients,
  selectedRecipientId,
  initiativeId,
  onClose,
}: {
  recipients: Recipient[]
  selectedRecipientId?: number
  initiativeId: number
  onClose: VoidFunction
}) => {
  const client = useClient()
  const { showSuccessToast, showFailToast } = useToast()
  const translations = useTranslations().app.dashboard.messages
  const [selectedRecipients, setSelectedRecipients] = useState<number[]>(
    selectedRecipientId ? [selectedRecipientId] : [],
  )
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [showSelectionModal, setShowSelectionModal] = useState(false)

  const [files, setFiles] = useState<File[]>([])
  const [isSending, setIsSending] = useState(false)
  const isSendingRef = useRef(false)

  const canSend =
    (message.trim() !== '' || files.length > 0) &&
    selectedRecipients.length > 0 &&
    !isSending

  const send = useCallback(() => {
    if (isSendingRef.current) {
      return
    }

    isSendingRef.current = true
    setIsSending(true)
    client
      .call('start-conversation', {
        recipientIds: selectedRecipients,
        initiativeId: initiativeId,
        subject: subject.trim(),
        message,
      })
      .then(async (messageIds) => {
        if (!messageIds || messageIds.length === 0) {
          showFailToast()
          isSendingRef.current = false
          setIsSending(false)
          return
        }

        if (files.length > 0) {
          const uploads = files.flatMap((file) =>
            messageIds.map((messageId: number) =>
              client.stream('upload-file', {
                mimeType: file.type,
                contents: file,
                size: file.size,
                fileName: `${file.name}-${Date.now()}`,
                payload: { messageFile: { id: messageId } },
              }),
            ),
          )
          await Promise.all(uploads)
        }

        isSendingRef.current = false
        showSuccessToast(translations.messageSent)
        onClose()
      })
      .catch((error) => {
        console.error(error)
        showFailToast()
        isSendingRef.current = false
        setIsSending(false)
      })
  }, [
    selectedRecipients,
    subject,
    message,
    initiativeId,
    files,
    client,
    translations.messageSent,
    onClose,
    showSuccessToast,
    showFailToast,
  ])

  const displayedRecipients = useMemo(() => {
    const recipientMap = new Map(recipients.map((r) => [r.id, r]))
    return selectedRecipients
      .map((id) => recipientMap.get(id))
      .filter((r): r is Recipient => r !== undefined)
  }, [recipients, selectedRecipients])

  const onRemoveRecipient = useCallback((recipientId: number) => {
    setSelectedRecipients((prev) => prev.filter((id) => id !== recipientId))
  }, [])

  if (showSelectionModal) {
    return (
      <RecipientSelectionModal
        recipients={recipients}
        selectedRecipientIds={selectedRecipients}
        onClose={() => setShowSelectionModal(false)}
        onConfirm={(ids) => {
          setSelectedRecipients(ids)
          setShowSelectionModal(false)
        }}
      />
    )
  }

  return (
    <div className="d e ad dh at t al dz e0"
    >
      <Row
        style={{
          padding: '1rem',
          background: 'var(--palette-white)',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--gray-100)',
        }}
      >
        <Title.H5>{translations.newMessage}</Title.H5>
        <CloseButton onClick={onClose} size="small" />
      </Row>
      <div className="e1 e2"
      >
        <BorderlessTextInput
          label={translations.subject}
          text={subject}
          setText={setSubject}
        />
      </div>
      <Row
        style={{
          padding: '0.5rem 1rem',
          borderBottom: '1px solid var(--gray-100)',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        {displayedRecipients.map((recipient) => (
          <RecipientTag
            key={recipient.id}
            {...recipient}
            onRemove={() => onRemoveRecipient(recipient.id)}
          />
        ))}
        {recipients.length > selectedRecipients.length && (
          <Button
            size="small"
            variant="secondary"
            icon={faPlus}
            ariaLabel={translations.addRecipients}
            onClick={() => setShowSelectionModal(true)}
          />
        )}
      </Row>
      <MultiLineTextInput
        value={message}
        setValue={setMessage}
        style={{
          border: 0,
          '&:focus': {
            outline: 'none',
            border: 0,
          },
          resize: 'none',
          height: '18rem',
        }}
      />
      <Row
        gap={'0.5rem'}
        style={{
          display: 'flex',
          padding: '1rem 1.5rem',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid var(--gray-100)',
        }}
      >
        <Row
          gap={'0.5rem'}
          style={{
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          <FileSelector
            maxFiles={5}
            maxSizeMB={10}
            allowedFileTypes={[
              'image/jpeg',
              'image/png',
              'image/gif',
              'image/webp',
              'application/pdf',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'application/vnd.ms-powerpoint',
              'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            ]}
            containerStyle={{
              cursor: 'pointer',
            }}
            onFileSelectionUpdated={setFiles}
            disabled={isSending}
          />
          <Row
            gap={'0.5rem'}
            style={{
              alignItems: 'center',
              overflowX: 'scroll',
            }}
          >
            {files.map((file, i) => (
              <FileDisplay
                key={i}
                type={'document'}
                name={file.name}
                onRemove={
                  isSending
                    ? undefined
                    : () => setFiles((prev) => prev.toSpliced(i, 1))
                }
                isLoading={isSending}
              />
            ))}
          </Row>
        </Row>
        <Button
          size="small"
          label={translations.sendMessage}
          onClick={() => {
            void send()
          }}
          disabled={!canSend}
          style={{ flexShrink: 0 }}
        />
      </Row>
    </div>
  )
}
